import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ArclipseBrandLockup from '../components/ArclipseBrandLockup';
import ConstellationBackdrop from '../components/ConstellationBackdrop';
import './LegalPages.css';

const EFFECTIVE_DATE = '10 June 2026';

function LegalLayout({ eyebrow, title, intro, children }) {
  return (
    <div className="legal-root">
      <ConstellationBackdrop />
      <header className="legal-nav">
        <Link to="/" className="legal-brand" aria-label="ARCLIPSE Constellation home">
          <ArclipseBrandLockup size="sm" />
        </Link>
        <Link to="/" className="legal-back">
          <ArrowLeft size={14} />
          <span>Back</span>
        </Link>
      </header>

      <main className="legal-layout">
        <aside className="legal-index">
          <span>{eyebrow}</span>
          <p>Effective {EFFECTIVE_DATE}</p>
          <nav aria-label="Legal pages">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </nav>
        </aside>

        <article className="legal-document">
          <header>
            <p>{eyebrow}</p>
            <h1>{title}</h1>
            <div>{intro}</div>
          </header>
          {children}
          <footer>
            <Link to="/terms">Terms of Use</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <span>2026 ARCLIPSE</span>
          </footer>
        </article>
      </main>
    </div>
  );
}

export function TermsPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Terms of Use"
      intro="These terms govern access to the ARCLIPSE Constellation website, waitlist, application process and related communications."
    >
      <section>
        <h2>1. Agreement</h2>
        <p>
          By using this website or submitting information, you agree to these Terms and acknowledge our
          {' '}<Link to="/privacy">Privacy Policy</Link>. If you submit on behalf of a company, you confirm
          that you are authorised to do so.
        </p>
      </section>

      <section>
        <h2>2. Constellation applications</h2>
        <p>
          Constellation is a selective founder programme. An application, waitlist entry, review, meeting
          or ARC signal does not guarantee admission, funding, investment, introductions, commercial work
          or any other outcome. We may accept, decline, pause or request more information about an application.
        </p>
      </section>

      <section>
        <h2>3. No investment or professional advice</h2>
        <p>
          Website content and application feedback are general information only. Nothing on this website is
          financial, legal, tax, engineering, scientific or investment advice, an offer of securities, or a
          commitment to invest. Any future programme, investment, equity or commercial arrangement requires
          a separate written agreement signed by the relevant parties.
        </p>
      </section>

      <section>
        <h2>4. Your information and materials</h2>
        <p>
          You retain ownership of the information, pitch materials and intellectual property you submit.
          You give ARCLIPSE a limited, non-exclusive permission to store, reproduce and internally share those
          materials only as reasonably required to assess your company, administer Constellation and communicate
          with you.
        </p>
        <p>
          Application submissions are not confidential unless a separate written confidentiality agreement
          says otherwise. Do not submit trade secrets, source code or information you are not authorised to share.
          We will handle personal information under our Privacy Policy.
        </p>
      </section>

      <section>
        <h2>5. Accurate and lawful use</h2>
        <p>
          You must provide information that is accurate to the best of your knowledge and must not impersonate
          another person, infringe rights, introduce malicious code, interfere with the service or use it for
          unlawful activity. You may update or withdraw your application by contacting us.
        </p>
      </section>

      <section>
        <h2>6. ARC signal and human review</h2>
        <p>
          The ARC signal is an informational founder-context tool, not a clinical, psychological or medical
          assessment. It may support review, but final Constellation decisions are made by people and are not
          based solely on automated processing.
        </p>
      </section>

      <section>
        <h2>7. Availability and external services</h2>
        <p>
          We may change, suspend or discontinue parts of the website or application process. The service may
          contain links to third-party services that ARCLIPSE does not control. Their own terms and privacy
          practices apply.
        </p>
      </section>

      <section>
        <h2>8. Liability</h2>
        <p>
          To the maximum extent permitted by law, ARCLIPSE is not liable for indirect, incidental, special or
          consequential loss arising from use of the website or an application decision. Nothing in these Terms
          excludes rights, guarantees or remedies that cannot lawfully be excluded, including under the
          Australian Consumer Law.
        </p>
      </section>

      <section>
        <h2>9. Changes to these Terms</h2>
        <p>
          We may update these Terms for future use of the service. The effective date will be shown above.
          Material changes will not retrospectively alter a separately signed agreement.
        </p>
      </section>

      <section>
        <h2>10. Governing law</h2>
        <p>
          These Terms are governed by the laws of New South Wales, Australia. Courts in New South Wales have
          non-exclusive jurisdiction. Mandatory rights available to you under the law where you live continue
          to apply.
        </p>
      </section>

      <section>
        <h2>11. Contact</h2>
        <p>
          Questions, withdrawal requests and legal notices may be sent to
          {' '}<a href="mailto:legal@arclipse.com">legal@arclipse.com</a>.
        </p>
      </section>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout
      eyebrow="Privacy"
      title="Privacy Policy"
      intro="This policy explains how ARCLIPSE handles personal information collected through Constellation."
    >
      <section>
        <h2>1. What we collect</h2>
        <p>
          We may collect your name, email, location, professional profiles, company and domain information,
          application answers, pitch links, correspondence, programme activity, ARC signal responses and
          technical information such as device, browser, IP address and website usage.
        </p>
      </section>

      <section>
        <h2>2. Why we collect it</h2>
        <p>
          We use personal information to operate the waitlist and application process, assess founder and company
          fit, communicate decisions and opportunities, administer the programme, protect the service, maintain
          records, improve Constellation and comply with law. Where applicable, we rely on your consent, steps
          requested before entering an agreement, our legitimate operational interests and legal obligations.
        </p>
      </section>

      <section>
        <h2>3. How decisions are made</h2>
        <p>
          Applications are reviewed by people. ARCLIPSE may use structured scoring, software or the ARC signal
          to organise information and support review, but does not currently make final admission decisions solely
          through automated processing.
        </p>
      </section>

      <section>
        <h2>4. How we share information</h2>
        <p>
          We may share information with personnel and advisers who need it for review, and with service providers
          supporting hosting, databases, email, analytics, security and programme administration. We may also
          disclose information when required by law, to protect rights and safety, or as part of a genuine business
          restructure. We do not sell personal information.
        </p>
        <p>
          We do not share your application with angels, investors or commercial partners for their independent
          use without telling you or obtaining permission where required.
        </p>
      </section>

      <section>
        <h2>5. International handling</h2>
        <p>
          ARCLIPSE operates from Australia and may use service providers that process information in other
          countries, including the United States and other locations where our infrastructure providers operate.
          We take reasonable steps to use appropriate contractual, organisational and security safeguards.
        </p>
      </section>

      <section>
        <h2>6. Retention and security</h2>
        <p>
          We retain information only for as long as reasonably needed for the purposes above, legal obligations,
          dispute handling and legitimate programme records. We use reasonable technical and organisational
          measures to protect information, but no online system can guarantee absolute security.
        </p>
      </section>

      <section>
        <h2>7. Your choices and rights</h2>
        <p>
          Depending on where you live, you may request access, correction, deletion, restriction, portability or
          objection, and may withdraw consent for future processing. You may also withdraw from the waitlist or
          application process. Some records may be retained where law or legitimate recordkeeping requires it.
        </p>
      </section>

      <section>
        <h2>8. Cookies and analytics</h2>
        <p>
          We may use essential storage and limited analytics to keep the service working, understand performance
          and prevent abuse. Where local law requires it, non-essential cookies will be used only after the
          required choice is provided.
        </p>
      </section>

      <section>
        <h2>9. Children</h2>
        <p>
          Constellation is intended for founders aged 18 or older. We do not knowingly collect application
          information from children.
        </p>
      </section>

      <section>
        <h2>10. Contact and complaints</h2>
        <p>
          To exercise a privacy right or make a complaint, contact
          {' '}<a href="mailto:privacy@arclipse.com">privacy@arclipse.com</a>. We aim to respond within 30 days.
          If an Australian privacy concern remains unresolved, you may contact the Office of the Australian
          Information Commissioner. You may also contact the privacy regulator where you live.
        </p>
      </section>
    </LegalLayout>
  );
}
